/**
 * Feature Flag Service
 * Simple service to fetch and cache feature flags for the current session
 */

import { runtimeConfig } from './RuntimeConfigService';

export interface SessionFeatures {
  emblemEnabled: boolean;
}

class FeatureFlagService {
  private features: SessionFeatures | null = null;
  private featuresPromise: Promise<SessionFeatures> | null = null;

  /**
   * Get feature flags for the current session
   * Caches results for the session duration
   */
  async getSessionFeatures(): Promise<SessionFeatures> {
    // Return cached features if already loaded
    if (this.features) {
      return this.features;
    }

    // Return existing promise if features are being loaded
    if (this.featuresPromise) {
      return this.featuresPromise;
    }

    // Start loading features
    this.featuresPromise = this.loadSessionFeatures();
    this.features = await this.featuresPromise;
    return this.features;
  }

  private async loadSessionFeatures(): Promise<SessionFeatures> {
    try {
      const config = await runtimeConfig.loadConfig();
      const sessionId = config.sessionId;

      if (!sessionId) {
        console.warn('[FeatureFlags] No session ID available, defaulting to disabled features');
        return { emblemEnabled: false };
      }

      const apiUrl = config.apiUrl;
      const response = await fetch(`${apiUrl}/api/v1/sessions/${sessionId}/features`);

      if (!response.ok) {
        console.warn('[FeatureFlags] Failed to fetch features, defaulting to disabled', {
          status: response.status,
          statusText: response.statusText,
        });
        return { emblemEnabled: false };
      }

      const features = await response.json();
      console.log('[FeatureFlags] Loaded session features:', features);
      
      return {
        emblemEnabled: features.emblemEnabled || false,
      };
    } catch (error) {
      console.error('[FeatureFlags] Error loading session features:', error);
      // Default to safe state on error
      return { emblemEnabled: false };
    }
  }

  /**
   * Check if Emblem integration is enabled
   */
  async isEmblemEnabled(): Promise<boolean> {
    const features = await this.getSessionFeatures();
    return features.emblemEnabled;
  }

  /**
   * Reset cached features (useful for testing)
   */
  reset(): void {
    this.features = null;
    this.featuresPromise = null;
  }
}

// Export singleton instance
export const featureFlagService = new FeatureFlagService();